import React, { useEffect, useState } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { api } from '../api/client.ts';
import { openTelegramStarsInvoice, triggerHaptic } from '../telegram/sdk.ts';
import type { StarProduct, PlayerEntitlement } from '@galaxy/shared';

export const StarsShopView: React.FC = () => {
  const { token, refreshState, authStatus } = useGame();
  const activeToken = token || api.getToken();
  const [products, setProducts] = useState<StarProduct[]>([]);
  const [entitlements, setEntitlements] = useState<PlayerEntitlement[]>([]);
  const [ownedProductIds, setOwnedProductIds] = useState<string[]>([]);
  const [equipped, setEquipped] = useState<{
    ship_skin?: string | null;
    combat_vfx?: string | null;
    base_theme?: string | null;
    galaxy_deco?: string | null;
  }>({});
  const [isVip, setIsVip] = useState<boolean>(false);
  const [vipExpiresAt, setVipExpiresAt] = useState<number | null>(null);
  const [starsMode, setStarsMode] = useState<string>('TEST');
  const [shopEnabled, setShopEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [processingProduct, setProcessingProduct] = useState<string | null>(null);
  const [selectedPreview, setSelectedPreview] = useState<StarProduct | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [trialStatus, setTrialStatus] = useState<{
    is_admin: boolean;
    max_trials: number;
    trials_used: number;
    trials_remaining: number;
    active_trials: Array<{
      id: string;
      product_id: string;
      category: string;
      trial_index: number;
      created_at: number;
      expires_at: number;
      is_active: boolean;
      remaining_hours: number;
    }>;
  } | null>(null);
  const [trialLoadingProduct, setTrialLoadingProduct] = useState<string | null>(null);
  const [confirmProduct, setConfirmProduct] = useState<StarProduct | null>(null);
  const [adminActionLoading, setAdminActionLoading] = useState<string | null>(null);

  // Load shop products, player entitlements, and equipped cosmetics
  const loadShopData = async () => {
    try {
      setLoading(true);
      const prodData = await api.getStarsProducts().catch((err) => {
        console.error('Failed to get stars products:', err);
        return null;
      });

      if (prodData?.success) {
        setProducts(prodData.products || []);
        setStarsMode(prodData.stars_mode || 'TEST');
        setShopEnabled(prodData.shop_enabled ?? true);
      }

      if (activeToken) {
        const [entData, eqData, trData] = await Promise.all([
          api.getStarsEntitlements().catch(() => null),
          api.getStarsEquipped().catch(() => null),
          api.getCosmeticTrialStatus().catch(() => null),
        ]);

        if (entData?.success) {
          setEntitlements(entData.entitlements || []);
          setOwnedProductIds(entData.owned_products || []);
          setIsVip(entData.is_vip || false);
          setVipExpiresAt(entData.vip_expires_at || null);
        }

        if (eqData?.success && eqData.equipped) {
          setEquipped(eqData.equipped);
        }

        if (trData?.success) {
          setTrialStatus(trData);
        }
      }
    } catch (err: any) {
      console.error('Failed to load stars shop data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShopData();
  }, [token, activeToken, authStatus]);

  // Handle Buy Product
  const handleBuy = async (product: StarProduct) => {
    if (!shopEnabled) {
      setMessage({ type: 'error', text: 'Cửa hàng Stars hiện đang tạm bảo trì!' });
      return;
    }

    if (!activeToken) {
      setMessage({ type: 'error', text: 'Vui lòng đăng nhập để thực hiện thanh toán!' });
      return;
    }

    if (product.is_reward_only || product.stars_price <= 0) {
      setMessage({
        type: 'info',
        text: '🎁 Đây là vật phẩm độc quyền nhận qua mốc giới thiệu bạn bè tại tab THƯỞNG, không bán bằng Telegram Stars!',
      });
      return;
    }

    // Limited LIVE: Either entire shop is LIVE, or Admin testing Skin Tàu Void Striker (15 Stars)
    const isLiveAllowedForProduct = starsMode === 'LIVE' || (trialStatus?.is_admin && product.id === 'skin_ship_void_striker');

    if (!isLiveAllowedForProduct) {
      triggerHaptic('warning');
      setMessage({
        type: 'info',
        text: '🔒 Chế độ xem trước an toàn: Thanh toán Stars thật hiện đang trong giai đoạn thử nghiệm giới hạn cho Admin (Skin Tàu Void Striker). Hãy bấm "🎯 DÙNG THỬ 24H" hoặc "🧪 MUA THỬ (ADMIN)" để trải nghiệm miễn phí!',
      });
      return;
    }

    setProcessingProduct(product.id);
    setMessage(null);
    triggerHaptic('medium');

    try {
      // 1. Request Invoice Link from Server
      const invData = await api.createStarsInvoice(product.id);
      if (!invData.success) {
        throw new Error((invData as any).error || 'Không thể tạo đơn hàng Stars');
      }

      const { invoice_link, order_id, test_mode } = invData;

      // 2. Open invoice link in Telegram or simulate test payment
      const isTgInvoice = invoice_link && invoice_link.startsWith('https://t.me/$');
      const isInsideTg = typeof window !== 'undefined' && Boolean(window.Telegram?.WebApp && typeof window.Telegram.WebApp.openInvoice === 'function');

      if (isTgInvoice && isInsideTg) {
        // Open official Telegram Stars native payment sheet
        const paymentResult = await openTelegramStarsInvoice(invoice_link);
        if (paymentResult === 'paid') {
          triggerHaptic('success');
          setMessage({
            type: 'success',
            text: `Thanh toán thành công! Bạn đã nhận [${product.name}].`,
          });
          await loadShopData();
          await refreshState();
        } else if (paymentResult === 'cancelled') {
          setMessage({ type: 'info', text: 'Bạn đã hủy giao dịch Stars.' });
        } else if (paymentResult === 'failed') {
          setMessage({ type: 'error', text: 'Giao dịch thanh toán Stars thất bại hoặc bị từ chối.' });
        } else {
          setMessage({ type: 'info', text: 'Đang chờ Telegram xử lý giao dịch...' });
        }
      } else if (test_mode) {
        // TEST Mode in browser or dev environment: Automatic sandbox payment simulation
        const simData = await api.simulateTestPayment(order_id);
        if (simData.success) {
          triggerHaptic('success');
          setMessage({
            type: 'success',
            text: `[CHẾ ĐỘ TEST] Thanh toán ${product.name} (${product.stars_price} ⭐) thành công!`,
          });
          await loadShopData();
          await refreshState();
        } else {
          throw new Error((simData as any).error || 'Mô phỏng thanh toán thất bại');
        }
      } else {
        // LIVE Mode outside Telegram
        const paymentResult = await openTelegramStarsInvoice(invoice_link);
        if (paymentResult === 'paid') {
          triggerHaptic('success');
          setMessage({
            type: 'success',
            text: `Thanh toán thành công! Bạn đã nhận [${product.name}].`,
          });
          await loadShopData();
          await refreshState();
        } else if (paymentResult === 'cancelled') {
          setMessage({ type: 'info', text: 'Bạn đã hủy giao dịch.' });
        } else {
          setMessage({ type: 'error', text: 'Giao dịch chưa hoàn tất hoặc bị gián đoạn.' });
        }
      }
    } catch (err: any) {
      triggerHaptic('error');
      setMessage({ type: 'error', text: err.message || 'Lỗi thanh toán Telegram Stars' });
    } finally {
      setProcessingProduct(null);
    }
  };

  const calculateDaysRemaining = (exp: number | null): number => {
    if (!exp) return 0;
    const diff = exp - Date.now();
    return Math.max(0, Math.ceil(diff / (86400 * 1000)));
  };

  const handleEquipToggle = async (product: StarProduct) => {
    if (!activeToken) return;
    const isCurrentlyEquipped = equipped[product.category as keyof typeof equipped] === product.id;
    const targetId = isCurrentlyEquipped ? null : product.id;

    try {
      const data = await api.equipCosmetic(product.category, targetId);
      if (data.success) {
        triggerHaptic('success');
        setEquipped((prev) => ({
          ...prev,
          [product.category]: targetId,
        }));
        setMessage({
          type: 'success',
          text: targetId ? `Đã trang bị ${product.name}!` : `Đã tháo trang bị ${product.name}!`,
        });
        await refreshState();
      } else {
        throw new Error((data as any).error || 'Không thể thay đổi trang bị');
      }
    } catch (err: any) {
      triggerHaptic('error');
      setMessage({ type: 'error', text: err.message || 'Lỗi trang bị ngoại trang' });
    }
  };

  const handleActivateTrial = async (product: StarProduct) => {
    if (!activeToken) {
      if (authStatus === 'authenticating') {
        setMessage({ type: 'info', text: 'Đang xác thực Telegram, vui lòng thử lại sau giây lát...' });
        return;
      }
      setMessage({ type: 'error', text: 'Vui lòng mở Mini App từ Telegram để kích hoạt dùng thử!' });
      return;
    }
    setTrialLoadingProduct(product.id);
    setMessage(null);
    triggerHaptic('medium');
    try {
      const res = await api.activateCosmeticTrial(product.id);
      if (res.success) {
        triggerHaptic('success');
        setMessage({
          type: 'success',
          text: `🎯 Kích hoạt dùng thử 24h thành công cho [${res.trial?.product_name || product.name}]! Đã tự động trang bị vào hạm đội.`,
        });
        await loadShopData();
        await refreshState();
      } else {
        throw new Error((res as any).error || 'Không thể kích hoạt dùng thử');
      }
    } catch (err: any) {
      triggerHaptic('error');
      setMessage({
        type: 'error',
        text: err.message || 'Lỗi khi kích hoạt dùng thử ngoại trang',
      });
    } finally {
      setTrialLoadingProduct(null);
    }
  };

  // Handle Admin Simulated Test Purchase (0 Stars)
  const handleAdminTestPurchase = async (product: StarProduct) => {
    if (!activeToken) {
      setMessage({ type: 'error', text: 'Vui lòng đăng nhập với tư cách Admin!' });
      return;
    }
    setAdminActionLoading(product.id);
    setMessage(null);
    triggerHaptic('medium');
    try {
      const res = await api.adminTestPurchase(product.id);
      if (res.success) {
        triggerHaptic('success');
        setMessage({
          type: 'success',
          text: `🧪 [ADMIN TEST] Đã mô phỏng mua thành công [${product.name}] (0 Stars)! Quyền sở hữu đã lưu vào kho và tự động trang bị.`,
        });
        await loadShopData();
        await refreshState();
      } else {
        throw new Error((res as any).error || 'Mô phỏng mua thất bại');
      }
    } catch (err: any) {
      triggerHaptic('error');
      setMessage({
        type: 'error',
        text: err.message || 'Lỗi khi thực hiện mua thử Admin',
      });
    } finally {
      setAdminActionLoading(null);
    }
  };

  // Handle Admin Clear Test Entitlements
  const handleClearAdminTestPurchases = async () => {
    if (!activeToken) return;
    if (!window.confirm('Bạn có chắc muốn dọn sạch tất cả quyền mua thử nghiệm (ADMIN_TEST) của tài khoản không?')) {
      return;
    }
    setLoading(true);
    triggerHaptic('medium');
    try {
      const res = await api.clearAdminTestPurchases();
      if (res.success) {
        triggerHaptic('success');
        setMessage({
          type: 'success',
          text: `🧹 ${res.message}`,
        });
        await loadShopData();
        await refreshState();
      } else {
        throw new Error((res as any).error || 'Không thể dọn dẹp quyền mua thử');
      }
    } catch (err: any) {
      triggerHaptic('error');
      setMessage({ type: 'error', text: err.message || 'Lỗi khi dọn dẹp quyền mua thử' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 12, maxWidth: 800, margin: '0 auto', paddingBottom: 60 }}>
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(5, 8, 17, 0.95) 100%)',
        border: '1px solid rgba(234, 179, 8, 0.4)',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 16px rgba(234, 179, 8, 0.08)',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 24 }}>⭐</span>
            <h2 style={{ margin: 0, fontSize: 18, color: '#fde047', letterSpacing: 0.5 }}>
              CỬA HÀNG TELEGRAM STARS
            </h2>
            <span style={{
              background: starsMode === 'LIVE' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(234, 179, 8, 0.2)',
              border: `1px solid ${starsMode === 'LIVE' ? '#22c55e' : '#eab308'}`,
              color: starsMode === 'LIVE' ? '#4ade80' : '#fde047',
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
            }}>
              {starsMode === 'LIVE' ? 'CHÍNH THỨC (XTR)' : '🔒 XEM TRƯỚC AN TOÀN'}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-dim)' }}>
            Xem trước ngoại trang tàu chiến, hiệu ứng laser và tiện ích chỉ huy. Tuyệt đối không bán chỉ số pay-to-win.
          </p>
        </div>

        {isVip && (
          <div style={{
            background: 'rgba(234, 179, 8, 0.15)',
            border: '1px solid #eab308',
            borderRadius: 6,
            padding: '6px 10px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, color: '#fde047', fontWeight: 700 }}>ĐẶC QUYỀN VIP</div>
            <div style={{ fontSize: 12, color: '#fff', fontWeight: 700 }}>
              Còn {calculateDaysRemaining(vipExpiresAt)} ngày
            </div>
          </div>
        )}
      </div>

      {/* 24H Cosmetic Trial Status Banner */}
      {trialStatus && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(15, 23, 42, 0.9) 100%)',
          border: '1px solid rgba(6, 182, 212, 0.35)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>🎯</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8', letterSpacing: 0.3 }}>
                {trialStatus.is_admin
                  ? '👑 ĐẶC QUYỀN ADMIN: DÙNG THỬ NGOẠI TRANG KHÔNG GIỚI HẠN'
                  : '🎯 ƯU ĐÃI TRẢI NGHIỆM: 2 LƯỢT DÙNG THỬ NGOẠI TRANG MIỄN PHÍ (24H/LƯỢT)'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                {trialStatus.is_admin
                  ? 'Tài khoản Quản trị viên được dùng thử toàn bộ skin tàu, laser và hiệu ứng chiến đấu miễn phí không giới hạn.'
                  : `Bạn được chọn trải nghiệm tối đa 2 vật phẩm ngoại trang (24 giờ/lượt). Sau 2 lượt, mua bằng Telegram Stars để sở hữu vĩnh viễn.`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {trialStatus.is_admin && (
              <button
                onClick={handleClearAdminTestPurchases}
                style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#f87171',
                  padding: '5px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                }}
              >
                🧹 DỌN SẠCH QUYỀN MUA TEST
              </button>
            )}
            <div style={{
              background: trialStatus.is_admin ? 'rgba(234, 179, 8, 0.15)' : (trialStatus.trials_remaining > 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)'),
              border: `1px solid ${trialStatus.is_admin ? '#eab308' : (trialStatus.trials_remaining > 0 ? '#22c55e' : '#ef4444')}`,
              color: trialStatus.is_admin ? '#fde047' : (trialStatus.trials_remaining > 0 ? '#4ade80' : '#f87171'),
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>
              {trialStatus.is_admin ? 'Lượt thử: Vô hạn 👑' : `Còn lại: ${trialStatus.trials_remaining} / 2 lượt`}
            </div>
          </div>
        </div>
      )}

      {/* Message alert */}
      {message && (
        <div style={{
          padding: '10px 14px',
          borderRadius: 6,
          marginBottom: 16,
          fontSize: 13,
          fontWeight: 600,
          background: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : message.type === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)',
          border: `1px solid ${message.type === 'success' ? '#22c55e' : message.type === 'error' ? '#ef4444' : '#3b82f6'}`,
          color: message.type === 'success' ? '#4ade80' : message.type === 'error' ? '#f87171' : '#60a5fa',
        }}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
          Đang nạp danh mục Cửa Hàng Stars...
        </div>
      ) : products.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: 'rgba(11, 18, 33, 0.85)',
          border: '1px solid rgba(0, 229, 255, 0.2)',
          borderRadius: 8,
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⭐</div>
          <div style={{ fontSize: 14, color: '#f87171', marginBottom: 16 }}>
            Chưa thể tải danh mục sản phẩm Stars từ máy chủ.
          </div>
          <button
            className="sci-btn"
            onClick={loadShopData}
            style={{
              padding: '8px 20px',
              fontSize: 12,
              fontWeight: 700,
              background: 'rgba(0, 229, 255, 0.2)',
              borderColor: 'var(--accent-cyan)',
              color: '#ffffff',
            }}
          >
            🔄 TẢI LẠI CỬA HÀNG
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 14,
          marginBottom: 20,
        }}>
          {products.map((p) => {
            const isPermanentlyOwned = p.is_one_time && entitlements.some((e) => e.product_id === p.id && !e.expires_at);
            const activeTrial = trialStatus?.active_trials?.find((t) => t.product_id === p.id);
            const isVipProduct = p.category === 'vip_pass';
            const isProcessing = processingProduct === p.id;
            const isTrialLoading = trialLoadingProduct === p.id;
            const isCosmetic = ['ship_skin', 'combat_vfx', 'base_theme', 'galaxy_deco'].includes(p.category);
            const isAdmin = Boolean(trialStatus?.is_admin);
            const isEquipped = equipped[p.category as keyof typeof equipped] === p.id;

            // Luồng A: Điều kiện dùng thử miễn phí 24h
            // - Admin: Luôn được dùng thử hoặc gia hạn thử 24h mọi ngoại trang (kể cả đang thử hoặc đã sở hữu để test)
            // - Người chơi: Được thử nếu chưa sở hữu vĩnh viễn, chưa có active trial, và còn lượt thử (> 0)
            const canTrial = isCosmetic && (
              isAdmin || (!isPermanentlyOwned && !activeTrial && (trialStatus?.trials_remaining ?? 0) > 0)
            );

            return (
              <div
                key={p.id}
                style={{
                  background: 'rgba(11, 18, 33, 0.85)',
                  border: `1px solid ${p.preview_data.border_glow || 'rgba(0, 229, 255, 0.2)'}`,
                  borderRadius: 8,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: `0 4px 12px ${p.preview_data.border_glow || 'rgba(0,0,0,0.4)'}`,
                  transition: 'transform 0.15s ease',
                }}
              >
                <div>
                  {/* Card top badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{
                      background: isPermanentlyOwned
                        ? 'rgba(34, 197, 94, 0.2)'
                        : activeTrial
                        ? 'rgba(234, 179, 8, 0.25)'
                        : (p.preview_data.accent_color ? `${p.preview_data.accent_color}22` : 'rgba(0, 229, 255, 0.15)'),
                      color: isPermanentlyOwned
                        ? '#4ade80'
                        : activeTrial
                        ? '#fde047'
                        : (p.preview_data.accent_color || 'var(--accent-cyan)'),
                      border: `1px solid ${
                        isPermanentlyOwned
                          ? '#22c55e'
                          : activeTrial
                          ? '#eab308'
                          : (p.preview_data.accent_color || 'var(--accent-cyan)')
                      }`,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}>
                      {isPermanentlyOwned ? '✓ ĐÃ SỞ HỮU' : activeTrial ? `⏳ ĐANG THỬ (còn ${activeTrial.remaining_hours}h)` : p.preview_badge}
                    </span>
                    <span style={{ fontSize: 20 }}>{p.preview_data.icon_symbol || '⭐'}</span>
                  </div>

                  <h3 style={{ margin: '0 0 6px 0', fontSize: 16, color: 'var(--text-bright)' }}>
                    {p.name}
                  </h3>

                  <p style={{ margin: '0 0 12px 0', fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.4 }}>
                    {p.description}
                  </p>

                  {/* Highlights */}
                  {p.preview_data.features && (
                    <ul style={{ margin: '0 0 12px 0', paddingLeft: 16, fontSize: 11, color: '#cbd5e1' }}>
                      {p.preview_data.features.map((f, idx) => (
                        <li key={idx} style={{ marginBottom: 3 }}>{f}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Actions & Price */}
                <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
                  {/* Top utility row: Price & Preview / Equip */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: '#fde047', fontSize: 16 }}>⭐</span>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#fde047' }}>
                        {p.stars_price}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Stars</span>
                    </div>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        onClick={() => setSelectedPreview(p)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#cbd5e1',
                          borderRadius: 4,
                          padding: '6px 10px',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          touchAction: 'manipulation',
                        }}
                      >
                        👁️ Xem thử
                      </button>

                      {/* Equipment toggle if owned or active trial */}
                      {(isPermanentlyOwned || activeTrial) && !isVipProduct && (
                        isEquipped ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <span style={{
                              background: 'rgba(34, 197, 94, 0.2)',
                              border: '1px solid #22c55e',
                              color: '#4ade80',
                              borderRadius: 4,
                              padding: '6px 8px',
                              fontSize: 11,
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}>
                              ✓ ĐANG DÙNG
                            </span>
                            <button
                              onClick={() => handleEquipToggle(p)}
                              style={{
                                background: 'rgba(239, 68, 68, 0.15)',
                                border: '1px solid rgba(239, 68, 68, 0.4)',
                                color: '#f87171',
                                borderRadius: 4,
                                padding: '6px 8px',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                touchAction: 'manipulation',
                              }}
                            >
                              Tháo
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEquipToggle(p)}
                            style={{
                              background: 'rgba(6, 182, 212, 0.2)',
                              border: '1px solid #06b6d4',
                              color: '#22d3ee',
                              borderRadius: 4,
                              padding: '6px 10px',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                              touchAction: 'manipulation',
                            }}
                          >
                            TRANG BỊ
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  {/* Main Action Buttons: DÙNG THỬ (Luồng A) vs MUA STARS (Luồng B) */}
                  <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                    {/* LUỒNG A: DÙNG THỬ MIỄN PHÍ 24H (Không gọi invoice, không trừ Stars) */}
                    {canTrial && (
                      <button
                        onClick={() => handleActivateTrial(p)}
                        disabled={isTrialLoading}
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.25) 0%, rgba(14, 116, 144, 0.35) 100%)',
                          border: '1.5px solid #06b6d4',
                          color: '#38bdf8',
                          borderRadius: 6,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: isTrialLoading ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          boxShadow: '0 2px 8px rgba(6, 182, 212, 0.2)',
                          touchAction: 'manipulation',
                        }}
                      >
                        <span>🎯</span>
                        <span>
                          {isTrialLoading
                            ? 'Đang kích hoạt...'
                            : isAdmin
                            ? (activeTrial ? 'THỬ LẠI / GIA HẠN 24H (ADMIN)' : 'DÙNG THỬ 24H (ADMIN)')
                            : `DÙNG THỬ MIỄN PHÍ 24H (Còn ${trialStatus?.trials_remaining ?? 0}/2)`}
                        </span>
                      </button>
                    )}

                    {/* LUỒNG A.2: MUA THỬ (ADMIN) — 0 STARS (Mô phỏng sở hữu vĩnh viễn kiểm tra kho, không tạo hóa đơn Telegram) */}
                    {isAdmin && (
                      <button
                        onClick={() => handleAdminTestPurchase(p)}
                        disabled={adminActionLoading === p.id}
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(109, 40, 217, 0.35) 100%)',
                          border: '1.5px solid #a855f7',
                          color: '#c084fc',
                          borderRadius: 6,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: adminActionLoading === p.id ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          boxShadow: '0 2px 8px rgba(168, 85, 247, 0.2)',
                          touchAction: 'manipulation',
                        }}
                      >
                        <span>🧪</span>
                        <span>
                          {adminActionLoading === p.id ? 'Đang cấp quyền test...' : 'MUA THỬ (ADMIN) — 0 STARS'}
                        </span>
                      </button>
                    )}

                    {/* PHẦN B: Ẩn nút mua đối với vật phẩm độc quyền nhận thưởng (0 Stars) */}
                    {(p.is_reward_only || p.stars_price <= 0) ? (
                      <div style={{
                        width: '100%',
                        background: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#34d399',
                        borderRadius: 6,
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 700,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}>
                        <span>🎁</span>
                        <span>ĐỘC QUYỀN: NHẬN TẠI TAB THƯỞNG</span>
                      </div>
                    ) : isPermanentlyOwned ? (
                      isVipProduct ? (
                        <button
                          onClick={() => setConfirmProduct(p)}
                          disabled={isProcessing}
                          style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
                            border: 'none',
                            color: '#050811',
                            borderRadius: 6,
                            padding: '8px 12px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: isProcessing ? 'wait' : 'pointer',
                            touchAction: 'manipulation',
                          }}
                        >
                          {isProcessing ? 'Đang xử lý...' : '+30 Ngày VIP'}
                        </button>
                      ) : (
                        <div style={{
                          textAlign: 'center',
                          fontSize: 11,
                          color: '#4ade80',
                          fontWeight: 600,
                          padding: '4px 0',
                        }}>
                          ✓ Đã sở hữu vĩnh viễn trong kho
                        </div>
                      )
                    ) : (
                      <button
                        onClick={() => setConfirmProduct(p)}
                        disabled={isProcessing}
                        style={{
                          width: '100%',
                          background: isAdmin && p.id === 'skin_ship_void_striker'
                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
                          border: isAdmin && p.id === 'skin_ship_void_striker' ? '1.5px solid #fbbf24' : 'none',
                          color: '#050811',
                          borderRadius: 6,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: isProcessing ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 4,
                          boxShadow: isAdmin && p.id === 'skin_ship_void_striker' ? '0 0 12px rgba(245, 158, 11, 0.4)' : undefined,
                          touchAction: 'manipulation',
                        }}
                      >
                        <span>⭐</span>
                        <span>
                          {isProcessing
                            ? 'Đang xử lý...'
                            : isAdmin && p.id === 'skin_ship_void_striker'
                            ? 'MUA THẬT (15 STARS - LIVE TEST)'
                            : activeTrial
                            ? `Mua vĩnh viễn (${p.stars_price} Stars)`
                            : `Mua (${p.stars_price} Stars)`}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Support & Terms */}
      <div style={{
        background: 'rgba(5, 8, 17, 0.7)',
        border: '1px solid var(--border-panel)',
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        fontSize: 12,
        color: 'var(--text-dim)',
      }}>
        <div>
          <span>🛡️ Giao dịch được bảo mật bởi Telegram Bot Payments (XTR).</span>
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          <button
            onClick={() => setShowTermsModal(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--accent-cyan)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 12,
              padding: 0,
            }}
          >
            Chính sách & Hoàn tiền (/terms)
          </button>
          <a
            href="https://t.me/GalaxyTelegramNews"
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--accent-cyan)', textDecoration: 'underline' }}
          >
            Hỗ trợ (/paysupport)
          </a>
        </div>
      </div>

      {/* Product Preview Modal */}
      {selectedPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: 'var(--bg-panel)',
            border: `2px solid ${selectedPreview.preview_data.accent_color || 'var(--accent-cyan)'}`,
            borderRadius: 10,
            padding: 20,
            maxWidth: 420,
            width: '100%',
            boxShadow: `0 8px 32px ${selectedPreview.preview_data.border_glow || 'rgba(0,0,0,0.8)'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>{selectedPreview.preview_data.icon_symbol || '🛸'}</span>
              <button
                onClick={() => setSelectedPreview(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-dim)',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <h3 style={{ margin: '0 0 4px 0', fontSize: 18, color: 'var(--text-bright)' }}>
              {selectedPreview.name}
            </h3>
            <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: selectedPreview.preview_data.accent_color || 'var(--accent-cyan)',
              marginBottom: 12,
            }}>
              {selectedPreview.preview_badge}
            </div>

            {/* Interactive Sci-Fi visual simulation */}
            <div style={{
              background: '#030712',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              marginBottom: 16,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute',
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: selectedPreview.preview_data.accent_color || 'var(--accent-cyan)',
                filter: 'blur(50px)',
                opacity: 0.25,
              }} />
              <span style={{ fontSize: 44, zIndex: 1 }}>{selectedPreview.preview_data.icon_symbol || '🛸'}</span>
              <div style={{ fontSize: 11, color: '#94a3b8', zIndex: 1, marginTop: 6 }}>
                Mô phỏng giao diện hiển thị trong thiên hà
              </div>
            </div>

            <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.5, marginBottom: 16 }}>
              {selectedPreview.description}
            </p>

            {(() => {
              const isCosmeticPreview = ['ship_skin', 'combat_vfx', 'base_theme', 'galaxy_deco'].includes(selectedPreview.category);
              const isPermOwned = selectedPreview.is_one_time && entitlements.some((e) => e.product_id === selectedPreview.id && !e.expires_at);
              const activeTrialInPreview = trialStatus?.active_trials?.find((t) => t.product_id === selectedPreview.id);
              const isAdmin = Boolean(trialStatus?.is_admin);
              const canTrialInPreview = isCosmeticPreview && (
                isAdmin || (!isPermOwned && !activeTrialInPreview && (trialStatus?.trials_remaining ?? 0) > 0)
              );

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Luồng A: Nút dùng thử 24h miễn phí trực tiếp trong modal xem thử */}
                  {canTrialInPreview && (
                    <button
                      onClick={async () => {
                        const prod = selectedPreview;
                        setSelectedPreview(null);
                        await handleActivateTrial(prod);
                      }}
                      disabled={trialLoadingProduct === selectedPreview.id}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.3) 0%, rgba(14, 116, 144, 0.45) 100%)',
                        border: '1.5px solid #06b6d4',
                        color: '#38bdf8',
                        padding: '12px 14px',
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        boxShadow: '0 4px 14px rgba(6, 182, 212, 0.3)',
                        touchAction: 'manipulation',
                      }}
                    >
                      <span>🎯</span>
                      <span>
                        {isAdmin
                          ? (activeTrialInPreview ? 'THỬ LẠI / GIA HẠN 24H (ADMIN)' : 'DÙNG THỬ MIỄN PHÍ 24H (ADMIN)')
                          : `DÙNG THỬ MIỄN PHÍ 24H (Còn ${trialStatus?.trials_remaining ?? 0}/2 lượt)`}
                      </span>
                    </button>
                  )}

                  {/* Luồng A.2: Nút Mua thử giả lập 0 Stars cho Admin trong modal */}
                  {isAdmin && (
                    <button
                      onClick={async () => {
                        const prod = selectedPreview;
                        setSelectedPreview(null);
                        await handleAdminTestPurchase(prod);
                      }}
                      disabled={adminActionLoading === selectedPreview.id}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(109, 40, 217, 0.45) 100%)',
                        border: '1.5px solid #a855f7',
                        color: '#c084fc',
                        padding: '12px 14px',
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        boxShadow: '0 4px 14px rgba(168, 85, 247, 0.3)',
                        touchAction: 'manipulation',
                      }}
                    >
                      <span>🧪</span>
                      <span>MUA THỬ (ADMIN) — 0 STARS</span>
                    </button>
                  )}

                  {/* Luồng B: Mua Stars hoặc Đóng */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => setSelectedPreview(null)}
                      style={{
                        flex: 1,
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'var(--text-dim)',
                        padding: '10px 0',
                        borderRadius: 6,
                        fontWeight: 600,
                        cursor: 'pointer',
                        touchAction: 'manipulation',
                      }}
                    >
                      Đóng
                    </button>
                    {(selectedPreview.is_reward_only || selectedPreview.stars_price <= 0) ? (
                      <div style={{
                        flex: 2,
                        background: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#34d399',
                        padding: '10px 0',
                        borderRadius: 6,
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}>
                        <span>🎁</span>
                        <span>Nhận tại Tab Thưởng</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          const p = selectedPreview;
                          setSelectedPreview(null);
                          setConfirmProduct(p);
                        }}
                        style={{
                          flex: 2,
                          background: isAdmin && selectedPreview.id === 'skin_ship_void_striker'
                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
                          border: isAdmin && selectedPreview.id === 'skin_ship_void_striker' ? '1.5px solid #fbbf24' : 'none',
                          color: '#050811',
                          padding: '10px 0',
                          borderRadius: 6,
                          fontWeight: 700,
                          cursor: 'pointer',
                          boxShadow: isAdmin && selectedPreview.id === 'skin_ship_void_striker' ? '0 0 16px rgba(245, 158, 11, 0.4)' : undefined,
                          touchAction: 'manipulation',
                        }}
                      >
                        {isAdmin && selectedPreview.id === 'skin_ship_void_striker'
                          ? '⭐ Mua Thật (15 Stars - LIVE TEST)'
                          : `⭐ Mua ${selectedPreview.stars_price} Stars`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Terms & Refund Modal */}
      {showTermsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border-panel)',
            borderRadius: 10,
            padding: 20,
            maxWidth: 480,
            width: '100%',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text-bright)' }}>
                📜 Điều Khoản Dịch Vụ & Hoàn Tiền (Terms)
              </h3>
              <button
                onClick={() => setShowTermsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-dim)',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6, marginBottom: 16 }}>
              <p>• <strong>Vật phẩm kỹ thuật số:</strong> Tất cả vật phẩm mua bằng Telegram Stars (XTR) là tài sản ảo trong trò chơi, không có giá trị quy đổi sang tiền tệ thực tế ngoài đời.</p>
              <p>• <strong>Cân bằng công bằng (Non-P2W):</strong> Các vật phẩm Stars chỉ mang tính chất làm đẹp ngoại trang (skin), hiệu ứng chiến đấu (VFX) và tiện ích nhẹ. Tuyệt đối không làm thay đổi cán cân sức mạnh PvP.</p>
              <p>• <strong>Gói VIP 30 Ngày:</strong> Là gói mua một lần trong 30 ngày, không tự động gia hạn.</p>
              <p>• <strong>Chính sách Hoàn tiền (refundStarPayment):</strong> Trong trường hợp yêu cầu hoàn tiền được duyệt, số Stars sẽ được hoàn trả lại ví Telegram của bạn và các vật phẩm hoặc quyền lợi VIP tương ứng sẽ được tự động thu hồi.</p>
            </div>

            <button
              onClick={() => setShowTermsModal(false)}
              style={{
                width: '100%',
                background: 'rgba(0, 229, 255, 0.2)',
                border: '1px solid var(--accent-cyan)',
                color: 'var(--accent-cyan)',
                padding: '8px 0',
                borderRadius: 4,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ĐÃ HIỂU
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal before buying with Stars */}
      {confirmProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: 'var(--bg-panel)',
            border: '2px solid #eab308',
            borderRadius: 10,
            padding: 22,
            maxWidth: 440,
            width: '100%',
            boxShadow: '0 8px 32px rgba(234, 179, 8, 0.25)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>⭐</span>
                <h3 style={{ margin: 0, fontSize: 16, color: '#fde047', letterSpacing: 0.5 }}>
                  XÁC NHẬN MUA BẰNG TELEGRAM STARS
                </h3>
              </div>
              <button
                onClick={() => setConfirmProduct(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-dim)',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              background: 'rgba(11, 18, 33, 0.9)',
              border: '1px solid rgba(234, 179, 8, 0.3)',
              borderRadius: 8,
              padding: 14,
              marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 28 }}>{confirmProduct.preview_data.icon_symbol || '🛸'}</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#ffffff' }}>
                    {confirmProduct.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {confirmProduct.preview_badge} • {confirmProduct.category === 'vip_pass' ? 'Gói VIP 30 Ngày' : confirmProduct.category === 'boost' ? 'Vật phẩm tăng tốc' : 'Ngoại trang vĩnh viễn'}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 10,
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>Chi phí thanh toán:</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#fde047' }}>
                  {confirmProduct.stars_price} ⭐ Stars (XTR)
                </span>
              </div>
            </div>

            {confirmProduct.id === 'skin_ship_void_striker' && trialStatus?.is_admin ? (
              <div style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '2px solid #ef4444',
                borderRadius: 8,
                padding: 12,
                fontSize: 12,
                color: '#fca5a5',
                lineHeight: 1.5,
                marginBottom: 18,
              }}>
                <div style={{ fontWeight: 800, color: '#f87171', fontSize: 13, marginBottom: 4 }}>
                  ⚠️ CẢNH BÁO: GIAO DỊCH THẬT — TRỪ 15 STARS
                </div>
                <div>
                  Đây là giao dịch thật kết nối trực tiếp với ví Telegram của bạn. Sau khi bấm xác nhận, Telegram sẽ mở hóa đơn thanh toán và <strong>trừ 15 Telegram Stars (XTR)</strong> từ tài khoản Telegram của bạn.
                </div>
              </div>
            ) : (
              <div style={{
                background: 'rgba(234, 179, 8, 0.08)',
                border: '1px solid rgba(234, 179, 8, 0.25)',
                borderRadius: 6,
                padding: 10,
                fontSize: 11,
                color: '#cbd5e1',
                lineHeight: 1.5,
                marginBottom: 18,
              }}>
                {starsMode === 'LIVE' ? (
                  <>
                    ⚠️ <strong>Thanh toán chính thức:</strong> Sau khi bấm xác nhận, hệ thống sẽ mở hóa đơn thanh toán Telegram Stars (XTR) chính thức. Số Stars sẽ được trừ trực tiếp từ ví Telegram của bạn.
                  </>
                ) : (
                  <>
                    ℹ️ <strong>Môi trường Thử nghiệm:</strong> Hệ thống hiện đang chạy chế độ thử nghiệm an toàn trên Staging. Giao dịch mua thật đang khóa đối với vật phẩm này để bảo vệ tài khoản.
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmProduct(null)}
                style={{
                  flex: 1,
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'var(--text-dim)',
                  padding: '10px 0',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                }}
              >
                HỦY BỎ
              </button>
              <button
                onClick={() => {
                  const p = confirmProduct;
                  setConfirmProduct(null);
                  handleBuy(p);
                }}
                disabled={processingProduct === confirmProduct.id}
                style={{
                  flex: 2,
                  background: confirmProduct.id === 'skin_ship_void_striker' && trialStatus?.is_admin
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                    : 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
                  border: confirmProduct.id === 'skin_ship_void_striker' && trialStatus?.is_admin ? '1.5px solid #fbbf24' : 'none',
                  color: '#050811',
                  padding: '10px 0',
                  borderRadius: 6,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  boxShadow: confirmProduct.id === 'skin_ship_void_striker' && trialStatus?.is_admin ? '0 0 16px rgba(245, 158, 11, 0.5)' : undefined,
                  touchAction: 'manipulation',
                }}
              >
                {confirmProduct.id === 'skin_ship_void_striker' && trialStatus?.is_admin
                  ? 'XÁC NHẬN MUA THẬT (TRỪ 15 ⭐)'
                  : `XÁC NHẬN MUA (${confirmProduct.stars_price} ⭐)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
